/**
 * Internal dependencies
 */
import SpacingInputControl from './spacing-input-control';
import { ICONS, getPresetValueFromCustomValue, hasAxisSupport } from '../utils';
import { type SpacingSize } from '../../../utils';

const groupedSides = [ 'vertical', 'horizontal' ] as const;

type AxialInputControlsProps = {
	readonly minimumCustomValue: number;
	readonly onChange: ( values: Record< string, string | undefined > ) => void;
	readonly onMouseOut?: ( event: React.SyntheticEvent ) => void;
	readonly onMouseOver?: ( event: React.SyntheticEvent ) => void;
	readonly showSideInLabel?: boolean;
	readonly sides: ReadonlyArray< string >;
	readonly spacingSizes: Array< SpacingSize >;
	readonly type?: string;
	readonly values: Record< string, string | undefined >;
	readonly disabledSides: Record< 'vertical' | 'horizontal', boolean >;
};
export default function AxialInputControls( {
	minimumCustomValue,
	onChange,
	onMouseOut,
	onMouseOver,
	sides,
	spacingSizes,
	type,
	values,
	disabledSides,
}: AxialInputControlsProps ): JSX.Element {
	const createHandleOnChange = ( side: string ) => ( next?: string ) => {
		if ( ! onChange ) {
			return;
		}

		// Encode the existing value into the preset value if the passed in value matches the value of one of the spacingSizes.
		const nextValues: Record< string, string | undefined > = {
			...Object.keys( values ).reduce(
				( acc: Record< string, string | undefined >, key ) => {
					acc[ key ] = getPresetValueFromCustomValue(
						values[ key ],
						spacingSizes
					);
					return acc;
				},
				{}
			),
		};

		if ( side === 'vertical' ) {
			nextValues.top = next;
			nextValues.bottom = next;
		}

		if ( side === 'horizontal' ) {
			nextValues.left = next;
			nextValues.right = next;
		}

		onChange( nextValues );
	};

	// Filter sides if custom configuration provided, maintaining default order.
	const filteredSides = sides?.length
		? groupedSides.filter( ( side ) => hasAxisSupport( sides, side ) )
		: groupedSides;

	return (
		<>
			{ filteredSides.map( ( side ) => {
				const axisValue =
					side === 'vertical' ? values.top : values.left;
				return (
					<SpacingInputControl
						key={ `spacing-sizes-control-${ side }` }
						icon={ ICONS[ side as keyof typeof ICONS ] }
						minimumCustomValue={ minimumCustomValue }
						onChange={ createHandleOnChange( side ) }
						onMouseOut={ onMouseOut }
						onMouseOver={ onMouseOver }
						side={ side }
						spacingSizes={ spacingSizes }
						type={ type }
						value={ axisValue }
						disabled={ disabledSides[ side ] }
					/>
				);
			} ) }
		</>
	);
}
